import {
    defineStore,
} from 'pinia';
import {
    computed, reactive, toRefs,
} from 'vue';
import {
    useWorkExperienceStore,
} from './profile-work-experience-store';
import {
    useSkillStore,
} from './profile-skill-store';
import {
    useLanguageStore,
} from './profile-language-store';
import {
    useEducationStore,
} from './profile-education-store';
import {
    useDesiredJobStore,
} from './profile-desired-job-store';
import {
    useProfileBasicsStore,
} from './profile-basics-store';
import {
    useOriginStore,
} from './profile-origin-store';
import {
    createProfile as createProfileModel,
} from '../models/Profile';
// eslint-disable-next-line import/no-cycle
import {
    commitAndShowModal,
} from '../utils/error';
import {
    apiErrorShouldBeReported,
} from '../utils/api';
import {
    educationCategoriesGet, educationTypesGet,
} from '../services/education';
import {
    profileCreate, profileGet, updateProfileMeta,
} from '../services/profile';
import {
    ACTIONS, EVENTS, track,
} from '../utils/tracking';
import {
    useStatementStore,
} from './profile-statement-store';
import {
    useAttachmentStore,
} from './profile-attachment-store';
import {
    useAwardStore,
} from './profile-awards-store';
import {
    useBlacklistStore,
} from './profile-blacklist-store';
import {
    useInterestStore,
} from './profile-interest-store';
import {
    useProfileTextStore,
} from './profile-profile-text-store';
import {
    useProjectStore,
} from './profile-project-store';
import {
    useSoftSkillStore,
} from './profile-soft-skill-store';
import {
    useTrainingStore,
} from './profile-training-store';
import {
    useAuth,
} from './user-store';

// Use setup store for easier module integration
export const useProfileStore = defineStore('profile', () => {
    // Modules
    const origin = useOriginStore();
    const basics = useProfileBasicsStore();
    const desiredJob = useDesiredJobStore();
    const educations = useEducationStore();
    const languages = useLanguageStore();
    const skills = useSkillStore();
    const workExperiences = useWorkExperienceStore();
    const attachments = useAttachmentStore();
    const awards = useAwardStore();
    const blacklist = useBlacklistStore();
    const interests = useInterestStore();
    const profileText = useProfileTextStore();
    const projects = useProjectStore();
    const softSkills = useSoftSkillStore();
    const statement = useStatementStore();
    const trainings = useTrainingStore();

    const state = reactive({
        id: null,
        active: false,
        activeForm: false,
        anyFormDirty: false,
        boosterCounter: 0,
        cancelTimestamp: 0,
        completeness: 0,
        created: null,
        educationCategories: [],
        educationTypes: [],
        exists: false,
        fetched: false,
        hasSeenBooster: false,
        hasWorkExperience: false,
        image: [],
        newFormIsActive: false,
        profileIsVisible: false,
    });

    const auth = useAuth();
    const userWithEmailExists = computed(() => auth.isLoggedIn);

    const createRegisterProcessProfileModel = () => createProfileModel({
        basics: basics.model,
        desiredJob: desiredJob.model,
        educations: educations.models,
        origin: origin.origin,
        skills: skills.models,
        work: workExperiences.models,
    });

    const model = computed(() => createProfileModel({
        awards: awards.models,
        basics: basics.model,
        blacklist: blacklist.model,
        desiredJob: desiredJob.model,
        educations: educations.models,
        flags: {
            isActive: state.active,
            hasWorkExperience: state.hasWorkExperience,
            hasSeenBooster: state.hasSeenBooster,
            boosterCounter: state.boosterCounter,
            profileIsVisible: state.profileIsVisible,
        },
        interests: interests.models,
        languages: languages.models,
        origin: origin.origin,
        profileText: profileText.profileText,
        projects: projects.models,
        skills: skills.models,
        softSkills: softSkills.models,
        statement: statement.statement,
        trainings: trainings.models,
        work: workExperiences.models,
    }));

    const imageVersion = computed(() => version => state.image.find(x => x.version === version));

    function updateFormModules(response) {
        const {
            desiredJob: desiredJobData,
            profileText: profileTextData,
            attachments: attachmentData,
            awards: awardData,
            basics: basicsData,
            blacklist: blacklistData,
            educations: educationData,
            interests: interestData,
            languages: languageData,
            origin: originData,
            projects: projectData,
            skills: skillData,
            softSkills: softSkillData,
            statement: statementData,
            trainings: trainingData,
            work: workData,
        } = response?.data.data.attributes ?? {};

        if (attachmentData) attachments.updateFromQuery(attachmentData);
        if (awardData) awards.updateFromQuery(awardData);
        if (basicsData) basics.updateFromQuery(basicsData);
        if (blacklistData) blacklist.updateFromQuery(blacklistData);
        if (desiredJobData) desiredJob.updateFromQuery(desiredJobData);
        if (desiredJobData?.objectives?.length) {
            desiredJob.updateFromQuery(desiredJobData);
        }
        if (educationData) educations.updateFromQuery(educationData);
        if (interestData) interests.updateFromQuery(interestData);
        if (languageData) languages.updateFromQuery(languageData);
        if (originData) origin.setOrigin(originData);
        profileText.updateFromQuery(profileTextData);
        if (projectData) projects.updateFromQuery(projectData);
        if (skillData) skills.updateFromQuery(skillData);
        if (skillData?.length) {
            skills.updateFromQuery(skillData);
        }
        if (softSkillData) softSkills.updateFromQuery(softSkillData);
        statement.updateFromQuery(statementData);
        if (trainingData) trainings.updateFromQuery(trainingData);
        if (workData) workExperiences.updateFromQuery(workData);
    }

    function setProfileCreated(isCreated) {
        state.created = isCreated;
    }

    function setProfileFetched(isFetched) {
        state.fetched = Boolean(isFetched);
    }

    function setProfileId(id) {
        state.id = id;
    }

    function setProfileFlags(flags) {
        if (flags.isActive !== undefined) {
            state.active = flags.isActive;
        }
        if (flags.profileIsVisible !== undefined) {
            state.profileIsVisible = flags.profileIsVisible;
        }
        if (flags.hasWorkExperience !== undefined) {
            state.hasWorkExperience = flags.hasWorkExperience;
        }
        if (flags.exists !== undefined) {
            state.exists = flags.exists;
        }
        if (flags.hasSeenBooster !== undefined) {
            state.hasSeenBooster = flags.hasSeenBooster;
        }
        if (flags.boosterCounter !== undefined) {
            state.boosterCounter = flags.boosterCounter;
        }
    }

    function setProfileImage(image) {
        state.image = image;
    }

    function setProfileMeta(meta) {
        state.completeness = meta.completeness;
    }

    function setEducationCategories({ educationCategories }) {
        state.educationCategories = educationCategories;
    }

    function setEducationTypes({ educationTypes }) {
        state.educationTypes = educationTypes;
    }

    function setFormDirty(value) {
        state.anyFormDirty = value;
    }

    function setFormActive(value) {
        state.activeForm = value;
    }

    function setNewFormActive(value) {
        state.newFormIsActive = value;
    }

    function triggerCancel(timestamp) {
        state.cancelTimestamp = timestamp;
    }

    function updateMetadata(response) {
        setProfileId(response.data.data.id);
        setProfileFlags(response.data.data.attributes.flags);
        setProfileMeta(response.data.meta);
        setProfileFetched(true);
    }

    async function createProfile(profileModel, hash = null) {
        try {
            setProfileFetched(false);
            setProfileCreated(false);

            const response = await profileCreate(profileModel, hash);
            if (!response) return;

            updateFormModules(response);
            updateMetadata(response);

            setProfileImage(response.data.data.attributes.image);
            setProfileCreated(true);
        } catch (error) {
            setProfileFetched(false);
            setProfileCreated(false);
        }
    }

    async function fetchEducationCategories() {
        try {
            const educationCategories = await educationCategoriesGet();
            setEducationCategories({ educationCategories });
        } catch (error) {
            if (apiErrorShouldBeReported(error)) {
                commitAndShowModal(error);
            }
        }
    }

    async function fetchEducationTypes() {
        try {
            const educationTypes = await educationTypesGet();
            setEducationTypes({ educationTypes });
        } catch (error) {
            if (apiErrorShouldBeReported(error)) {
                commitAndShowModal(error);
            }
        }
    }

    async function fetchProfile() {
        try {
            setProfileFetched(false);

            const response = await profileGet();
            if (!response) return;

            updateFormModules(response);
            updateMetadata(response);

            setProfileImage(response.data.data.attributes.image);
        } catch (error) {
            setProfileFetched(false);
        }
    }

    async function updateProfile(profileData) {
        try {
            setProfileFetched(false);

            const response = await profileCreate(profileData);
            if (!response) return;

            updateFormModules(response);
            updateMetadata(response);

            setProfileImage(response.data.data.attributes.image);

            window.top.postMessage({
                type: 'event',
                action: 'profileUpdated',
                value: true,
            }, '*');
        } catch (error) {
            setProfileFetched(false);
        }
    }

    async function updateActiveStatus(status) {
        setProfileFlags({ profileIsVisible: status });

        await updateProfile(model.value);

        track({
            event: EVENTS.profileStatus,
            action: ACTIONS.conversion,
            label: status ? 'on' : 'off',
        });

        track({
            event: EVENTS.statusChange,
            element: 'Profile',
            elementDetail: status ? 'on' : 'off',
            ga4Event: true,
        });
    }

    async function updateHasWorkExperienceStatus(status) {
        setProfileFlags({ hasWorkExperience: status });
        await updateProfile(model.value);
    }

    async function updateHasSeenBooster(profileMeta) {
        setProfileFlags({ hasSeenBooster: Date.now() });
        await updateProfileMeta(profileMeta);
    }

    return {
        ...toRefs(state),
        model,

        // Modules
        origin,
        basics,
        desiredJob,
        educations,
        languages,
        skills,
        workExperiences,
        attachments,
        awards,
        blacklist,
        interests,
        profileText,
        projects,
        softSkills,
        statement,
        trainings,

        // Getters
        imageVersion,
        userWithEmailExists,

        // Actions
        createProfile,
        fetchEducationCategories,
        fetchEducationTypes,
        fetchProfile,
        updateProfile,
        updateHasSeenBooster,
        updateHasWorkExperienceStatus,
        updateActiveStatus,
        createRegisterProcessProfileModel,

        setProfileImage,
        setFormDirty,
        setFormActive,
        setNewFormActive,
        triggerCancel,
    };
});
